import { CommonModule } from '@angular/common'
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core'
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { RouterModule } from '@angular/router'
import { AnimationOptions } from 'ngx-lottie'
import { firstValueFrom, Subscription } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { CategoryService } from 'src/app/service/data/category.service'
import { DictionaryService } from 'src/app/service/data/dictionary.service'
import { TagService } from 'src/app/service/data/tag.service'
import { WordService } from 'src/app/service/data/word.service'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { Article, ArticlesByLanguage, Languages, PartOfSpeech, Themes } from '../../enums/enums'
import { SelectOption } from '../../interfaces/word'
import { DdcButtonSOneComponent } from '../ddc-button-s-one/ddc-button-s-one.component'
import { DdcDeleteDialogComponent } from '../ddc-delete-dialog/ddc-delete-dialog.component'
import { DdcElemChooserOneComponent } from '../ddc-elem-chooser-one/ddc-elem-chooser-one.component'
import { DdcTextGradientComponent } from '../ddc-text-gradient/ddc-text-gradient.component'

@Component({
  selector: 'ddc-edit-word-dialog',
  templateUrl: './ddc-edit-word-dialog.component.html',
  styleUrls: ['./ddc-edit-word-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    AngularMaterialsModules,
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    DdcTextGradientComponent,
    DdcElemChooserOneComponent,
    DdcButtonSOneComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DdcEditWordDialogComponent implements OnInit {
  @Output() wordDeleted = new EventEmitter<any>()

  _isLightTheme: boolean = true
  _isDarkTheme: boolean = false
  _isPaperTheme: boolean = false
  _theme: string
  _addedField: string = ''
  private themeSubscription: Subscription

  _isCreateBtnLoading = false
  _submitBtnText = 'Edit'
  _privacyMaxlength: number = 4
  _wordMaxlength: number = 100
  _exampleMaxlength: number = 5000
  _definitionMaxlength: number = 10000
  _posMaxlength: number = 3
  _articleMaxlength: number = 3
  _addError: string = ''

  _selectedChipOptions: string[] = []
  _selectedPrivacyChipOptions: string = ''
  _userTags: any[] = []
  _userCats: any[] = []
  _userDics: any[] = []

  _loadedCats: boolean = false
  _loadedTags: boolean = false
  _loadedDics: boolean = false

  _selectedTags: { id: number; label: string }[] = []
  _selectedDics: { id: number; label: string }[] = []
  _selectedArticle: string = null
  _selectedPos: string = null
  _selectedId: number = 0
  _selectedLanguage: string
  _selectedCategory: { id: number; label: string }[] = []

  languages: SelectOption[] = []
  articles: SelectOption[] = []
  poses: SelectOption[] = []

  _selectedLngHasArticle: boolean = false

  _isEditBtnLoading = false
  _isDialogLoading = true
  _maxLength: number = 100
  _editError: string = ''
  _editedField: string = ''

  _deletedField: boolean = false
  _deleteFromDDC: boolean = false
  _deleteFromDic: boolean = false

  _type: string = ''
  _element: any
  _editedWord: any
  _hasDic: boolean = false
  _targetDicId: number

  _isEditable: boolean = false
  _isWordEdited: boolean = false

  _form = new FormGroup({
    // ! Word
    _1_language: new FormControl('', [Validators.required]),

    _1_article: new FormControl(null, []),

    _1_word: new FormControl('', [Validators.required, Validators.maxLength(this._wordMaxlength)]),

    _1_pos: new FormControl(null, []),

    _1_example: new FormControl(null, [Validators.maxLength(this._exampleMaxlength)]),

    _1_definition: new FormControl(null, [Validators.maxLength(this._definitionMaxlength)]),

    // ! privacy
    _privacy: new FormControl('P01', [
      Validators.required,
      Validators.maxLength(this._privacyMaxlength),
    ]),
  })

  // This is the option that uses the package's AnimationOption interface
  options: AnimationOptions = {
    path: '/assets/lottie/green-tick.json',
    loop: false, // Set loop to false for a single playthrough
    autoplay: true,
  }

  partOfSpeeches: any

  constructor(
    private logger: DdcLoggerService,
    private fb: FormBuilder,
    public themeService: ThemeService,
    private cat_service: CategoryService,
    private tag_service: TagService,
    private word_service: WordService,
    private dic_service: DictionaryService,
    public dialog: MatDialog,
    private elRef: ElementRef,
    private editDialogRef: MatDialogRef<DdcEditWordDialogComponent>,
    private deleteDialogRef: MatDialogRef<DdcDeleteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    this._element = this.data.element

    if (this.data.hasDic) {
      this._hasDic = this.data.hasDic
      this._targetDicId = this.data.dicId
    }

    if (this.data.command === 'delete') {
      this.openDeleteDialog()
    }

    this._selectedPrivacyChipOptions = this._element.privacy
    this._selectedCategory[0] = this._element.category
    this._selectedTags = this._element.tags
    this._selectedDics = this._element.dictionaries
    this._selectedId = this._element.id

    this.themeSubscription = this.themeService.theme$.subscribe(theme => {
      this._isLightTheme = theme === Themes.LIGHT
      this._isDarkTheme = theme === Themes.DARK
      this._isPaperTheme = theme === Themes.PAPER
    })

    this.getLanguages()
    this.getArticles()
    this.getPos()
    this.getUserTags()
    this.getUserCats()
    this.getUserDics()

    // Initialize your form group
    this._form = this.fb.group({
      _1_language: [this._element.language],
      _1_article: [this._element.article],
      _1_word: [this._element.word],
      _1_pos: [this._element.pos],
      _1_example: [this._element.example],
      _1_definition: [this._element.definition],
      _privacy: [this._selectedPrivacyChipOptions],
    })

    // Set loading state to false once all data is loaded
    Promise.all([
      this.getLanguages(),
      this.getArticles(),
      this.getPos(),
      this.getUserTags(),
      this.getUserCats(),
      this.getUserDics(),
    ]).then(() => {
      setTimeout(() => {
        this._isDialogLoading = false
      }, 1000) // 1 second delay
    })
  }

  getPos(): void {
    this.logger.info('DdcEditWordDialogComponent: getPos()')

    const posArray = Object.values(PartOfSpeech) as string[]

    this.poses.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of posArray) {
      this.poses.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  updateSelectedTags(tags: any): void {
    this.logger.info('DdcEditWordDialogComponent: updateSelectedTags() | tags:', tags)
    this._selectedTags = tags
  }

  updateSelectedDics(dictionaries: any): void {
    this.logger.info(
      'DdcEditWordDialogComponent: updateSelectedDics() | dictionaries:',
      dictionaries,
    )
    this._selectedDics = dictionaries
  }

  updateSelectedCat(cat: any): void {
    this.logger.info('DdcEditWordDialogComponent: updateSelectedCat() | cat:', cat)
    this._selectedCategory = cat
  }

  getLanguages(): void {
    this.logger.info('DdcEditWordDialogComponent: getLanguages()')
    type Language = keyof typeof Languages
    this.languages = Object.entries(Languages).map(([key, [viewValue, rawValue]]) => ({
      raw_value: rawValue,
      view_value: viewValue,
    }))
  }

  onLanguageChange(event: any): void {
    this.logger.info('DdcEditWordDialogComponent: onLanguageChange() | event:', event)
    const selectedLng = event.value

    this.articles = []
    this.articles.push({
      raw_value: null,
      view_value: 'None',
    })

    const articlesArray = ArticlesByLanguage[selectedLng]

    if (articlesArray !== undefined && articlesArray.length > 0) {
      this._selectedLngHasArticle = true
      for (const value of articlesArray) {
        this.articles.push({
          raw_value: value,
          view_value: value,
        })
      }
    } else {
      this._selectedLngHasArticle = false
      this._selectedArticle = null
    }
  }

  initArticle(): void {
    this.logger.info('DdcEditWordDialogComponent: initArticle() | event:', event)
    const selectedLng = this._element.language

    this.articles = []
    this.articles.push({
      raw_value: null,
      view_value: 'None',
    })

    const articlesArray = ArticlesByLanguage[selectedLng]

    if (articlesArray != undefined) {
      this._selectedLngHasArticle = true
    } else {
      this._selectedLngHasArticle = false
      this._selectedArticle = null
    }

    if (articlesArray == undefined) return

    for (const value of articlesArray) {
      this.articles.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  getArticles(): void {
    this.logger.info('DdcEditWordDialogComponent: getArticles()')
    const articlesArray = Object.values(Article) as string[]

    this.articles.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of articlesArray) {
      this.articles.push({
        raw_value: value,
        view_value: value,
      })
    }

    this.initArticle()
  }

  getUserTags(): void {
    this.logger.info('DdcEditWordDialogComponent: getUserTags()')
    this.tag_service.get_tags().subscribe((res: any) => {
      if (res) {
        this._userTags = res
        this._loadedTags = true
      }
    })
  }

  getUserDics(): void {
    this.logger.info('DdcEditWordDialogComponent: getUserDics()')
    this.dic_service.get_dics(true).subscribe((res: any) => {
      if (res) {
        this._userDics = res
        this._loadedDics = true
      }
    })
  }

  getUserCats(): void {
    this.logger.info('DdcEditWordDialogComponent: getUserCats()')
    this.cat_service.get_cats().subscribe((res: any) => {
      if (res) {
        this._userCats = res
        this._loadedCats = true
      }
    })
  }

  hasNewCat(selectedCat: { id: number; label: string }[]): boolean {
    return !this._userCats.some(cat => cat.label.includes(selectedCat[0].label))
  }

  hasNewTags(selectedTags: { id: number; label: string }[]): { res: boolean; list: string[] } {
    const newTags: string[] = []

    for (const selectedTag of selectedTags) {
      const tagExists = this._userTags.some(userTag => userTag.label === selectedTag.label)

      if (!tagExists) {
        newTags.push(selectedTag.label)
      }
    }

    if (newTags.length == 0) return { res: false, list: [] }
    else return { res: true, list: newTags }
  }

  async handleCat(selectedCat: { id: number; label: string }[]): Promise<boolean> {
    try {
      if (
        selectedCat != null &&
        selectedCat.length != 0 &&
        selectedCat[0] != null &&
        this.hasNewCat(selectedCat)
      ) {
        const res: any = await firstValueFrom(this.cat_service.add_cat(selectedCat[0].label))
        if (res.success) {
          return true
        } else {
          if (res.res) {
            this._editError = res.res
          } else {
            this._editError = 'Cannot add new category: \"' + selectedCat[0].label + '\"'
          }
          this._isEditBtnLoading = false
          this._submitBtnText = 'Edit'
          return false
        }
      }
      return true
    } catch (error) {
      this.logger.error('DdcEditWordDialogComponent: handleCat() | error:', error)
      return false
    }
  }

  async handleTags(selectedTags: { id: number; label: string }[]): Promise<boolean> {
    try {
      if (
        selectedTags != null &&
        selectedTags.length != 0 &&
        selectedTags[0] != null &&
        this.hasNewTags(selectedTags).res
      ) {
        const res: { success: boolean; res: any }[] = []
        for (const tag of this.hasNewTags(selectedTags).list) {
          this.logger.info('DdcEditWordDialogComponent: handleTags() | tag:', tag)
          const res_elem: any = await firstValueFrom(this.tag_service.add_tag(tag))
          res.push(res_elem)
          if (res_elem.res) {
            this._editError = res_elem.res
            this._isEditBtnLoading = false
            this._submitBtnText = this._hasDic ? 'Add To Dictionary' : 'Add'
            return false
          }
          if (!res_elem.success) {
            this._editError = 'Cannot add new tag: \"' + tag + '\"'
            this._isEditBtnLoading = false
            this._submitBtnText = this._hasDic ? 'Add To Dictionary' : 'Add'
            return false
          }
        }
        const allTagsSuccess = res.every(result => result.success)

        if (allTagsSuccess) {
          return true
        } else {
          const failedTagsResults = res.filter(result => !result.success)
          if (failedTagsResults.length === 0) {
          } else {
            const failedLabels = failedTagsResults.map(result => result.res.label)
            this._editError = 'Cannot add new tag: \"' + failedLabels + '\"'
          }
          this._isEditBtnLoading = false
          this._submitBtnText = 'Edit'
          return false
        }
      }
      return true
    } catch (error) {
      this.logger.error('DdcEditWordDialogComponent: handleTags() | error:', error)
      return false
    }
  }

  async handleDics(selectedDics: { id: number; label: string }[]): Promise<boolean> {
    try {
      this.logger.info('DdcEditWordDialogComponent: handleDics() | selectedDics:', selectedDics)
      const res: any = await firstValueFrom(
        this.dic_service.edit_dics_of_word(this._element.id, selectedDics),
      )

      if (!res.success) {
        this._editError = 'Cannot edit dictionaries.'
        return false
      }
      return true
    } catch (error) {
      this.logger.error('DdcEditWordDialogComponent: handleDics() | error:', error)
      return false
    }
  }

  get w1(): any {
    return this._form.get('_1_word')
  }

  get def1(): any {
    return this._form.get('_1_definition')
  }

  get exp1(): any {
    return this._form.get('_1_example')
  }

  isFormValid(): boolean {
    const lng = this._form.get('_1_language').valid
    const art = this._form.get('_1_article').valid
    const word = this._form.get('_1_word').valid
    const pos = this._form.get('_1_pos').valid
    const exp = this._form.get('_1_example').valid
    const def = this._form.get('_1_definition').valid

    if (lng && art && word && pos && exp && def) {
      return true
    }

    return false
  }

  async editWord(): Promise<boolean> {
    this.logger.info('DdcEditWordDialogComponent: editWord()')
    try {
      this._editedWord = {
        id: this._selectedId,
        language: this._form.value._1_language,
        article: this._form.value._1_article,
        word: this._form.value._1_word,
        pos: this._form.value._1_pos,
        example: this._form.value._1_example,
        definition: this._form.value._1_definition,
        privacy: this._form.value._privacy,
        category: this._selectedCategory[0],
        tags: this._selectedTags,
      }

      const res: any = await firstValueFrom(this.word_service.edit_word(this._editedWord))

      this._isEditBtnLoading = false
      this._submitBtnText = 'Edit'

      if (res.success) {
        this._editError = ''
        this._editedField = res.res.word
        return true
      } else {
        this.logger.error('DdcEditWordDialogComponent: editWord() | failed: res:', res)
        this._editError = res.res
        this._editedField = ''
        return false
      }
    } catch (error) {
      this.logger.error('DdcEditWordDialogComponent: editWord() | error:', error)
      return false
    }
  }

  async edit(): Promise<void> {
    this.logger.info('DdcEditWordDialogComponent: edit()')
    if (!this.isFormValid()) return

    this._isEditBtnLoading = true
    if (this._selectedTags && this._selectedTags.length == 0) this._selectedTags = null

    if (!this._selectedCategory[0]) this._selectedCategory[0] = null

    try {
      const handledCat = await this.handleCat(this._selectedCategory)

      if (handledCat) {
        const handledTags = await this.handleTags(this._selectedTags)

        if (handledTags) {
          const handledDics = await this.handleDics(this._selectedDics)
          if (handledDics) {
            const handledWord = await this.editWord()

            if (handledWord) {
              this._isWordEdited = true
              // this._isEditBtnLoading = false
              // this.closeDialog(true); // Close dialog with edited flag set to true
            }
          } else {
            this.logger.error('handledDics: error')
          }
        } else {
          this.logger.error('handledTags: error')
        }
      } else {
        this.logger.error('handledCat: error')
      }
    } catch (error) {
      this.logger.error('DdcEditWordDialogComponent: edit() | error:', error)
    }
  }

  closeDialog(): void {
    this._isEditBtnLoading = false
    if (this._deletedField) this.editDialogRef.close({ removed: true, element: this.data.element })
    else this.editDialogRef.close({ edited: this._isWordEdited, element: this._editedWord })
  }

  onModeChange(isEditable: boolean): void {
    this._isEditable = isEditable
  }

  openDeleteDialog(): void {
    this.logger.info('DdcEditWordDialogComponent: openDeleteDialog()')

    // this.editDialogRef.close();

    const dialogRef = this.dialog.open(DdcDeleteDialogComponent, {
      data: {
        hasDic: this._hasDic,
        type: 'word',
        element: this._element,
      },
    })

    dialogRef.afterClosed().subscribe(result => {
      // Handle any actions after the dialog is closed, e.g., updating the user's profile picture.
      if (result === 'delete-element') {
        this.word_service.delete_word(this.data.element.id).subscribe((res: any) => {
          if (res.success) {
            this._editError = ''
            this._editedField = this._form.value._1_word
            this._deletedField = true
            this._deleteFromDDC = true
            this.wordDeleted.emit({ removed: true, element: this.data.element })
          } else {
            this.logger.error(
              'DdcEditWordDialogComponent: openDeleteDialog() | dialogRef.afterClosed() | delete_word: failed: res:',
              res,
            )
            this._editError = res.res
            this._editedField = ''
            this._deletedField = true
          }
          this._isEditBtnLoading = false
          this._submitBtnText = 'Edit'
        })
      } else if (result === 'delete-element-in-dic') {
        this.dic_service
          .remove_words_from_dic(this._targetDicId, [this.data.element.id])
          .subscribe((res: any) => {
            if (res.success) {
              this._editError = ''
              this._editedField = this._form.value._1_word
              this._deletedField = true
              this._deleteFromDic = true
              this.wordDeleted.emit({ removed: true, element: this.data.element })
            } else {
              this.logger.error(
                'DdcEditWordDialogComponent: openDeleteDialog() | dialogRef.afterClosed() | remove_words_from_dic: failed: res:',
                res,
              )
              this._editError = res.res
              this._editedField = ''
              this._deletedField = true
            }
            this._isEditBtnLoading = false
            this._submitBtnText = 'Edit'
          })
      }
    })
  }
}
